// Multi-region pollen registry — the "one world map, every region at once" core.
//
// ARCHITECTURE
// ------------
// Each regional dataset (Europe, North America, Asia) shares the SAME schema
// (equirect geo block + 52 weekly per-taxon R8 TEXTURE_2D_ARRAYs + a mask) but
// has DIFFERENT pixel dimensions and a DIFFERENT geographic extent. We render
// them on a single MapLibre map by instantiating the existing PollenLayer once
// PER region (each with its own id, geometry, GL resources and offset
// correction) and adding all of them to the map. MapLibre draws each custom
// layer in its OWN draw pass, which is exactly the texture-unit-safety property
// we want: a draw only ever binds the allergen arrays SELECTED IN THAT REGION
// (+ that region's mask + a dummy), so units-per-draw stay <= (selected-in-
// region) + 2, well inside the 16-unit floor, regardless of how many taxa are
// selected across all regions combined.
//
// UNIFIED ALLERGEN LIST (dedupe by stable taxon code)
// ---------------------------------------------------
// Many taxa are shared across regions (BETU birch, POAC grass, AMBR ragweed,
// ALNU alder, QUER oak, ...); some are region-specific (CRYJ sugi in Asia,
// GINK ginkgo in Asia, ACER maple / MORU mulberry in NA, ...). We merge every
// region's allergens into ONE list keyed by code: the first region that defines
// a code owns the friendly english/latin label, and we record WHICH regions
// carry that code. Selecting a code by its registry index lights it up in every
// region that has it (per-region MAX is then computed from that region's own
// selected arrays). Selection + URL state are therefore region-agnostic (by
// code), exactly as urlState.ts already assumes.

import type { Manifest, Allergen } from "./manifest";
import { loadManifest } from "./manifest";
import { decodeAllergenGeo, decodeMaskGeo, decodeMaskHires } from "./data";
import { PollenLayer } from "./pollenLayer";

// --- Cross-region taxon code aliasing ---------------------------------------
// The same plant is sometimes encoded under DIFFERENT codes across regions, so a
// naive "dedupe by code" leaves two selector chips for one taxon, each lighting
// only some regions. We map every regional code to a single CANONICAL code so
// the same plant becomes ONE chip that lights every region that has it. Per-
// region MAX is unaffected: a region's own decoded array is still addressed by
// its LOCAL code via RegionEntry.byCode (which keeps the raw codes); only the
// unified selector list + selection plumbing are canonicalized.
//
// Audited across all three manifests (EU / NA / Asia). The only genuine
// same-plant / different-code case is PINE:
//   - North America: PINA (Pinaceae, the pine family)
//   - East Asia:     PINU (Pinus, pines)
// Both are "Pine" for an allergy-overview product, so they merge to PINA.
// Everything else already shares a code across regions (ALNU alder, BETU birch,
// CORY hazel, FRAX ash, POAC grasses, AMBR ragweed, ARTE mugwort, PLAT
// plane/sycamore, CUPR cypress family, QUER oak, CAST sweet chestnut, ULMU elm)
// and so needs no alias. Genuinely region-specific taxa stay distinct: URTI
// (nettle, EU), ACER (maple, NA), MORU (mulberry, NA), POPU (cottonwood, NA),
// GINK (ginkgo, Asia), CRYJ (sugi, Asia), HUMU (Japanese hop, Asia).
const CODE_ALIASES: Readonly<Record<string, string>> = {
  PINU: "PINA", // East-Asia pine (Pinus) -> NA pine family code (Pinaceae)
};

/** Resolve a regional taxon code to its canonical (merged) code. */
export function canonicalCode(code: string): string {
  return CODE_ALIASES[code] ?? code;
}

/** A loaded region + its layer + a code->local-allergen-index lookup. */
export interface RegionEntry {
  key: string; // "eu" | "na" | "asia"
  layerId: string; // MapLibre layer id ("pollen", "pollen-na", ...)
  label: string; // human label ("Europe", "North America (CONUS)", ...)
  manifest: Manifest;
  base: string; // fetch base for this region's assets ("./data/", "./data/na/")
  layer: PollenLayer;
  byCode: Map<string, number>; // raw taxon code -> index into manifest.allergens
  /**
   * Canonical (merged) taxon code -> index into manifest.allergens. Lets a
   * unified/selector code (e.g. "PINA") address this region's local array even
   * when the region stores it under an aliased raw code (e.g. Asia's "PINU").
   */
  byCanonical: Map<string, number>;
  /** Approx. centre [lng,lat] for a "jump to region" affordance (optional UX). */
  center: [number, number];
}

/** One entry in the merged, deduplicated taxon list shown in the selector. */
export interface UnifiedAllergen {
  code: string; // stable taxon code (BETU, POAC, CRYJ, ...)
  english: string; // friendly name (from the first region that defines it)
  latin: string;
  /** Region keys that carry this taxon (e.g. ["eu","na"]). */
  regions: string[];
}

/** Static per-region presentation metadata (label + map centre). */
interface RegionSpec {
  key: string;
  url: string; // manifest URL (relative to the app base)
  base: string; // asset fetch base (derived; NOT the manifest dataDir — see note)
  layerId: string;
  fallbackLabel: string;
  center: [number, number];
}

// NOTE (manifest inconsistency, logged for follow-up): the Asia manifest's
// `dataDir` is "asia/" (missing the "data/" prefix the EU + NA manifests use),
// so trusting manifest.dataDir would 404 the Asia strips. We therefore derive
// each region's asset base from the KNOWN manifest location here rather than
// from manifest.dataDir. EU="./data/", NA="./data/na/", Asia="./data/asia/".
const REGION_SPECS: RegionSpec[] = [
  {
    key: "eu",
    url: "./data/manifest.json",
    base: "./data/",
    layerId: "pollen",
    fallbackLabel: "Europe",
    center: [15, 54],
  },
  {
    key: "na",
    url: "./data/na/manifest-na.json",
    base: "./data/na/",
    layerId: "pollen-na",
    fallbackLabel: "North America (CONUS)",
    center: [-96, 38],
  },
  {
    key: "asia",
    url: "./data/asia/manifest-asia.json",
    base: "./data/asia/",
    layerId: "pollen-asia",
    fallbackLabel: "Korea + Japan (East Asia)",
    center: [134, 36],
  },
  // ADDED region (Oceania) — purely data-driven from manifest-oceania.json.
  // All grid/geo/taxa/confidence values live IN the manifest (838x488 grid,
  // bbox lon 112-179 / lat -48..-9, 52 weekly slices, 13 taxa, top-level
  // confidence "literature"); RegionRegistry.load reads them at runtime. This
  // spec only declares the manifest LOCATION + presentation metadata (label is
  // taken from manifest.regionLabel "Oceania (Australia & New Zealand)" with
  // this fallback). It is NOT a baseline region: main.ts's BASELINE_REGION_KEYS
  // = {eu,na,asia} does not include "oceania", so decideMultiRegion treats it as
  // an added region (baseline:false) that absorbs VRAM pressure first, never
  // tightening the protected eu/na/asia caps. center is a representative SE-AU
  // point inside the manifest bbox for the "jump to region" affordance.
  {
    key: "oceania",
    url: "./data/oceania/manifest-oceania.json",
    base: "./data/oceania/",
    layerId: "pollen-oceania",
    fallbackLabel: "Oceania (Australia & New Zealand)",
    center: [145, -28],
  },
  // ADDED regions (Middle East, Rest-of-Asia, Latin America, Africa) — same
  // data-driven contract as Oceania: every grid/geo/taxa/confidence value lives
  // IN each manifest (RegionRegistry.load reads them at runtime); this spec only
  // declares the manifest LOCATION + presentation metadata. The label is taken
  // from manifest.regionLabel with this fallback. NONE is a baseline region:
  // main.ts's BASELINE_REGION_KEYS = {eu,na,asia} excludes these keys, so
  // decideMultiRegion treats each as an ADDED region (baseline:false) that
  // absorbs VRAM pressure first and never tightens the protected eu/na/asia caps.
  // `center` is a representative in-bbox land/data point for the "jump to region"
  // affordance. base derived from the KNOWN manifest location (NOT manifest
  // dataDir — see the note above; e.g. africa's dataDir is "data/africa/").
  {
    key: "me",
    url: "./data/me/manifest-me.json",
    base: "./data/me/",
    layerId: "pollen-me",
    fallbackLabel: "Middle East",
    center: [47, 30],
  },
  {
    key: "asia-rest",
    url: "./data/asia-rest/manifest-asia-rest.json",
    base: "./data/asia-rest/",
    layerId: "pollen-asia-rest",
    fallbackLabel: "Rest of Asia (China, S./SE. Asia, C. Asia, N. Asia)",
    center: [105, 35],
  },
  {
    key: "latam",
    url: "./data/latam/manifest-latam.json",
    base: "./data/latam/",
    layerId: "pollen-latam",
    fallbackLabel: "Latin America (S. America, Central America & Caribbean)",
    center: [-60, -25],
  },
  {
    key: "africa",
    url: "./data/africa/manifest-africa.json",
    base: "./data/africa/",
    layerId: "pollen-africa",
    fallbackLabel: "Africa",
    center: [25, -28],
  },
];

export class RegionRegistry {
  readonly regions: RegionEntry[] = [];
  /** Merged taxon list (dedup by code), in stable first-seen order. */
  readonly unified: UnifiedAllergen[] = [];
  /** code -> index into `unified`. */
  private unifiedByCode = new Map<string, number>();

  private constructor() {}

  /**
   * Load every region's manifest (in parallel) and build the merged taxon list.
   * A region that fails to load is skipped with a warning so a single bad
   * dataset never blocks the others (Europe stays the guaranteed baseline).
   */
  static async load(maxResident: number): Promise<RegionRegistry> {
    const reg = new RegionRegistry();
    const results = await Promise.all(
      REGION_SPECS.map(async (spec) => {
        try {
          const manifest = await loadManifestAt(spec.url);
          return { spec, manifest };
        } catch (err) {
          // eslint-disable-next-line no-console
          console.warn(`[regions] ${spec.key} manifest load failed:`, err);
          return null;
        }
      }),
    );

    for (const r of results) {
      if (!r) continue;
      const { spec, manifest } = r;
      const layer = new PollenLayer(manifest, maxResident, {
        id: spec.layerId,
        base: spec.base,
      });
      const byCode = new Map<string, number>();
      const byCanonical = new Map<string, number>();
      manifest.allergens.forEach((a, i) => {
        byCode.set(a.code, i);
        byCanonical.set(canonicalCode(a.code), i);
      });
      reg.regions.push({
        key: spec.key,
        layerId: spec.layerId,
        label: manifest.regionLabel ?? spec.fallbackLabel,
        manifest,
        base: spec.base,
        layer,
        byCode,
        byCanonical,
        center: spec.center,
      });
      reg.mergeAllergens(spec.key, manifest.allergens);
    }

    if (reg.regions.length === 0) {
      throw new Error("No region manifests could be loaded.");
    }
    return reg;
  }

  private mergeAllergens(regionKey: string, allergens: Allergen[]): void {
    for (const a of allergens) {
      // Merge by CANONICAL code so the same plant under different regional codes
      // (e.g. NA PINA / Asia PINU) becomes one selector entry.
      const code = canonicalCode(a.code);
      const existing = this.unifiedByCode.get(code);
      if (existing === undefined) {
        this.unifiedByCode.set(code, this.unified.length);
        this.unified.push({
          code,
          english: a.english,
          latin: a.latin,
          regions: [regionKey],
        });
      } else {
        const u = this.unified[existing];
        if (!u.regions.includes(regionKey)) u.regions.push(regionKey);
      }
    }
  }

  /** The merged taxon list shaped as `Allergen[]` for the selector grid. */
  unifiedAsAllergens(): Allergen[] {
    return this.unified.map((u) => ({
      code: u.code,
      english: u.english,
      latin: u.latin,
      files: [],
      defaultSelected: false,
    }));
  }

  /** Add every region's layer to the map (one custom layer / draw pass each). */
  addToMap(map: {
    addLayer: (l: PollenLayer) => void;
    getLayer: (id: string) => unknown;
  }): void {
    for (const r of this.regions) {
      if (!map.getLayer(r.layerId)) map.addLayer(r.layer);
    }
  }

  /** Load every region's mask (hi-res when present, else the data-grid mask). */
  async loadMasks(): Promise<void> {
    await Promise.all(
      this.regions.map(async (r) => {
        try {
          if (r.manifest.noData.maskFileHires) {
            const hi = await decodeMaskHires(r.manifest, r.base);
            r.layer.setMask(hi.data, hi.width, hi.height);
          } else {
            const g = r.layer.geometry;
            const m = await decodeMaskGeo(r.manifest, r.base, g.width, g.height);
            r.layer.setMask(m, g.width, g.height);
          }
        } catch (err) {
          // eslint-disable-next-line no-console
          console.warn(`[regions] ${r.key} mask load failed:`, err);
          // Fall back to the low-res mask if the hi-res decode threw.
          try {
            const g = r.layer.geometry;
            const m = await decodeMaskGeo(r.manifest, r.base, g.width, g.height);
            r.layer.setMask(m, g.width, g.height);
          } catch {
            /* no mask: region renders without an ocean cut-out (non-fatal) */
          }
        }
      }),
    );
  }

  /** Drive the shared 52-week timeline into every region. */
  setSlice(slice: number): void {
    for (const r of this.regions) r.layer.setSlice(slice);
  }

  setPaletteMode(mode: 0 | 1): void {
    for (const r of this.regions) r.layer.setPaletteMode(mode);
  }

  setPaintAll(on: boolean): void {
    for (const r of this.regions) r.layer.setPaintAll(on);
  }

  setOpacity(o: number): void {
    for (const r of this.regions) r.layer.setOpacity(o);
  }

  /** Apply the caps-decision residency cap to every region's layer. */
  setMaxResident(n: number): void {
    for (const r of this.regions) r.layer.setMaxResident(n);
  }

  /**
   * Change A — apply a PER-REGION residency cap (aligned to `this.regions`
   * order). decideMultiRegion gives the historically-shipped baseline regions
   * their old single-region cap and only tightens the newly-added regions under
   * VRAM pressure, so adding regions can never lower a baseline region's cap.
   *
   * Defensive fallback (R4): if the array length doesn't match `this.regions`
   * (it never should — decideMultiRegion builds perRegionCap as
   * `regions.map(...)`), we apply a single uniform cap. It MUST be the LARGEST
   * of the supplied caps — which is the baseline cap, since baseline regions
   * always receive the highest cap and only added regions are tightened — so
   * the fallback can NEVER degrade a baseline region below its protected
   * single-region `decide()` cap (the user's #1 hard rule). Using `Math.min`
   * here would have let an added region's lower cap leak onto EU/NA/East-Asia.
   */
  setMaxResidentPerRegion(caps: number[]): void {
    if (caps.length !== this.regions.length) {
      const fallback = caps.length > 0 ? Math.max(...caps) : 12;
      for (const r of this.regions) r.layer.setMaxResident(fallback);
      return;
    }
    this.regions.forEach((r, i) => r.layer.setMaxResident(caps[i]));
  }

  restoreAfterContextLoss(): void {
    for (const r of this.regions) r.layer.restoreAfterContextLoss();
  }

  /**
   * Ensure a taxon's CPU strips are decoded in every region that carries it.
   * Lazy-load on first selection: only regions missing the CPU copy fetch.
   * Returns once all relevant regions have the decoded copy cached.
   */
  async ensureCodeDecoded(code: string): Promise<void> {
    const canon = canonicalCode(code);
    await Promise.all(
      this.regions.map(async (r) => {
        // Alias-aware: a unified code (e.g. "PINA") resolves to this region's
        // local array even when stored under an aliased raw code (Asia "PINU").
        const idx = r.byCanonical.get(canon);
        if (idx === undefined) return; // region doesn't have this taxon
        if (r.layer.hasCpu(idx)) return; // already decoded
        const allergen = r.manifest.allergens[idx];
        const data = await decodeAllergenGeo(allergen, r.base, r.layer.geometry);
        r.layer.setCpu(idx, data);
      }),
    );
  }

  /**
   * Apply a code-based selection across ALL regions. For each region, the codes
   * are translated to that region's LOCAL allergen indices (skipping codes the
   * region doesn't have) and pushed to its layer. Per-region MAX is then a pure
   * uniform update from that region's own selected arrays. CPU copies for the
   * selected codes must already be decoded (call ensureCodeDecoded first).
   */
  setSelectionByCodes(codes: string[]): void {
    for (const r of this.regions) {
      const local: number[] = [];
      for (const c of codes) {
        // Alias-aware lookup so a merged code lights its aliased region too.
        const idx = r.byCanonical.get(canonicalCode(c));
        if (idx !== undefined && r.layer.hasCpu(idx)) local.push(idx);
      }
      r.layer.setSelection(local);
    }
  }

  /** Region keys that carry a given taxon code. */
  regionsForCode(code: string): string[] {
    const u = this.unifiedByCode.get(canonicalCode(code));
    return u === undefined ? [] : this.unified[u].regions.slice();
  }
}

/** Load + type a manifest from an arbitrary URL (not just ./data/manifest.json). */
async function loadManifestAt(url: string): Promise<Manifest> {
  // loadManifest(base) fetches `${base}manifest.json`; our region manifests have
  // varied filenames (manifest-na.json, manifest-asia.json), so fetch directly.
  // We do NOT call setSliceLayout here — the multi-region path uses per-region
  // SliceGeometry (geometryFromManifest), so the legacy globals stay untouched
  // (keeping the calibration tool's single-region assumptions intact).
  if (url.endsWith("/manifest.json") || url.endsWith("data/manifest.json")) {
    // The canonical EU manifest: reuse loadManifest so the legacy globals are
    // configured for any single-region consumer that still reads them.
    const base = url.replace(/manifest\.json$/, "");
    return loadManifest(base);
  }
  const r = await fetch(url);
  if (!r.ok) throw new Error(`${url} -> HTTP ${r.status}`);
  return (await r.json()) as Manifest;
}
